import React, { useState, useRef, useEffect } from "react";
import { useI18n } from "../../i18n/I18nProvider";
import { trackEvent } from "../../utils/analytics";
import {
  FloatingButton,
  ChatPanel,
  ChatHeader,
  CloseBtn,
  ChatMessages,
  MessageBubble,
  ChatInputContainer,
  SendButton,
  WelcomeMessage,
  TypingIndicator,
  ChatTooltip,
} from "./styles";

interface ChatMessage {
  role: "user" | "assistant";
  content: string;
}

const CHAT_API_URL = window.location.hostname === "localhost"
  ? "/api/chat"
  : "https://europe-west1-angelo-falci.cloudfunctions.net/chat";

function renderMessageContent(text: string, msgIndex: number): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  let key = 0;

  // Priority order: markdown link > bold > plain URL > phone number
  // - Markdown link:  [label](url)
  // - Bold:           **text**
  // - Plain URL:      https://... or www....  (excluding ) and ] to avoid eating markdown syntax)
  // - Phone:          +39 347 ...
  const inlineRe =
    /\[([^\]]*)\]\((https?:\/\/[^)]+)\)|\*\*([^*]+)\*\*|(https?:\/\/[^\s)\]]+|www\.[^\s)\]]+)|(\+?\d[\d\s().-]{4,}\d)/g;

  let lastIndex = 0;
  let match: RegExpExecArray | null;

  const pushText = (t: string) => {
    if (!t) return;
    const parts = t.split("\n");
    for (let i = 0; i < parts.length; i++) {
      if (parts[i].length > 0) nodes.push(<span key={`t-${msgIndex}-${key++}`}>{parts[i]}</span>);
      if (i < parts.length - 1) nodes.push(<br key={`br-${msgIndex}-${key++}`} />);
    }
  };

  while ((match = inlineRe.exec(text)) !== null) {
    const [full, mdLabel, mdUrl, boldContent, plainUrl, phone] = match;
    const idx = match.index;

    if (idx > lastIndex) {
      pushText(text.slice(lastIndex, idx));
    }

    if (mdLabel !== undefined) {
      // Markdown link [label](url)
      nodes.push(
        <a key={`ml-${msgIndex}-${key++}`} href={mdUrl} target="_blank" rel="noopener noreferrer">
          {mdLabel}
        </a>,
      );
    } else if (boldContent !== undefined) {
      // Bold **text**
      nodes.push(<strong key={`b-${msgIndex}-${key++}`}>{boldContent}</strong>);
    } else if (plainUrl !== undefined) {
      // Trim trailing punctuation from plain URLs (e.g. ".", ",", "!", ":")
      const trailingRe = /[)\]}.,:;!?]+$/;
      let url = plainUrl;
      let trailing = "";
      const tm = url.match(trailingRe);
      if (tm) {
        trailing = tm[0];
        url = url.slice(0, url.length - trailing.length);
      }
      const href = url.startsWith("http") ? url : `https://${url}`;
      nodes.push(
        <a key={`u-${msgIndex}-${key++}`} href={href} target="_blank" rel="noopener noreferrer">
          {url}
        </a>,
      );
      if (trailing) nodes.push(<span key={`tp-${msgIndex}-${key++}`}>{trailing}</span>);
    } else if (phone !== undefined) {
      const tel = phone.replace(/[^\d+]/g, "");
      nodes.push(
        <a key={`p-${msgIndex}-${key++}`} href={`tel:${tel}`}>
          {phone}
        </a>,
      );
    }

    lastIndex = idx + full.length;
  }

  if (lastIndex < text.length) {
    pushText(text.slice(lastIndex));
  }

  return nodes;
}

const CHAT_INTERACTED_KEY = "chat_interacted";

export function ChatWidget(): JSX.Element {
  const { locale, t } = useI18n();
  const [isOpen, setIsOpen] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(
    () => localStorage.getItem(CHAT_INTERACTED_KEY) === "1",
  );
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading]);

  useEffect(() => {
    if (hasInteracted) return;
    const showTimer = setTimeout(() => setShowTooltip(true), 2000);
    const hideTimer = setTimeout(() => setShowTooltip(false), 22000);
    return () => { clearTimeout(showTimer); clearTimeout(hideTimer); };
  }, [hasInteracted]);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || isLoading) return;

    const userMessage: ChatMessage = { role: "user", content: text };
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setInput("");
    setIsLoading(true);

    try {
      const response = await fetch(CHAT_API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          messages: updatedMessages.slice(-10),
          locale,
        }),
      });

      if (!response.ok) throw new Error("API error");

      const data = await response.json();
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: data.reply },
      ]);
      trackEvent("chat_message_sent", { locale });
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content:
            locale === "it"
              ? "Mi dispiace, non riesco a rispondere in questo momento. Riprova più tardi o contatta Angelo direttamente.\n(Potrebbero esserci limitazioni di utilizzo del servizio gratuito che alimenta il chatbot)"
              : "Sorry, I can't respond right now. Please try again later or contact Angelo directly.\n(There may be usage limitations of the free service that powers the chatbot)",
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const toggleChat = () => {
    setIsOpen((prev) => !prev);
    if (!isOpen) {
      setShowTooltip(false);
      if (!hasInteracted) {
        setHasInteracted(true);
        localStorage.setItem(CHAT_INTERACTED_KEY, "1");
      }
      trackEvent("chat_opened", { locale });
    }
  };

  return (
    <>
      <ChatTooltip $visible={showTooltip && !isOpen}>
        {t.chat?.tooltipHint}
      </ChatTooltip>
      <FloatingButton
        $isOpen={isOpen}
        $animated={!hasInteracted}
        onClick={toggleChat}
        aria-label={isOpen ? t.chat?.closeChat : t.chat?.openChat}
      >
        {isOpen ? (
          <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z" />
          </svg>
        )}
      </FloatingButton>

      {isOpen && (
        <ChatPanel>
          <ChatHeader>
            <h3>{t.chat?.title}</h3>
            <CloseBtn onClick={() => setIsOpen(false)} aria-label={t.chat?.closeChat}>
              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
              </svg>
            </CloseBtn>
          </ChatHeader>

          <ChatMessages>
            {messages.length === 0 && (
              <WelcomeMessage>{t.chat?.welcome}</WelcomeMessage>
            )}
            {messages.map((msg, i) => (
              <MessageBubble key={i} $role={msg.role}>
                {renderMessageContent(msg.content, i)}
              </MessageBubble>
            ))}
            {isLoading && (
              <TypingIndicator>
                <span />
              </TypingIndicator>
            )}
            <div ref={messagesEndRef} />
          </ChatMessages>

          <ChatInputContainer>
            <input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t.chat?.placeholder}
              maxLength={500}
            />
            <SendButton
              onClick={sendMessage}
              disabled={!input.trim() || isLoading}
            >
              {t.chat?.send}
            </SendButton>
          </ChatInputContainer>
        </ChatPanel>
      )}
    </>
  );
}
